import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Scrollbars from "react-custom-scrollbars-2";
import { SidebarData } from "../../data/json/sidebarData";
import ImageWithBasePath from "../imageWithBasePath";
import { useDispatch, useSelector } from "react-redux";
import { setExpandMenu } from "../../data/redux/commonSlice";
import { checkPermission } from "../../permissons/CheckPermissons"; // Adjust path as needed

// Define types
interface MenuItem {
  label: string;
  icon?: string;
  showSubRoute?: boolean;
  link?: string;
  submenu?: boolean;
  submenuItems?: MenuItem[];
  permission?: boolean | string;
  subLink1?: string;
  subLink2?: string;
  subLink3?: string;
  subLink4?: string;
  links?: string[];
}

interface SidebarSection {
  label: string;
  submenuOpen?: boolean;
  showSubRoute?: boolean;
  submenuHdr?: string;
  submenuItems: MenuItem[];
}

const Sidebar = () => {
  const Location = useLocation();
  const expandMenu = useSelector((state: any) => state.expandMenu);
  const dispatch = useDispatch();

  const [subOpen, setSubopen] = useState<any>("");
  const [subsidebar, setSubsidebar] = useState("");
  const [username, setUsername] = useState<string>("Unknown");
  const [group, setGroup] = useState<string>("Unknown");

  // Type guard for permission
  const hasStringPermission = (item: MenuItem): item is MenuItem & { permission: string } =>
    typeof item.permission === "string";

  useEffect(() => {
    const storedUsername = localStorage.getItem("username") || sessionStorage.getItem("username");
    const storedGroup = localStorage.getItem("group") || sessionStorage.getItem("group");
    console.log("Retrieved from storage:", { storedUsername, storedGroup });
    if (storedUsername) setUsername(storedUsername);
    if (storedGroup) setGroup(storedGroup);

    setSubopen(localStorage.getItem("menuOpened"));
    const submenus = document.querySelectorAll(".submenu");
    submenus.forEach((submenu) => {
      const listItems = submenu.querySelectorAll("li");
      submenu.classList.remove("active");
      listItems.forEach((item) => {
        if (item.classList.contains("active")) {
          submenu.classList.add("active");
          return;
        }
      });
    });
  }, [Location.pathname]);

  // Isolate mouse wheel scrolling inside sidebar so it never chains or scrolls the outer window
  useEffect(() => {
    const sidebarEl = document.getElementById("sidebar");
    if (!sidebarEl) return;

    const handleWheel = (e: WheelEvent) => {
      e.stopPropagation();
      const scrollable = (sidebarEl.querySelector('div[style*="overflow"]') || sidebarEl) as HTMLElement;
      if (scrollable) {
        const atTop = scrollable.scrollTop <= 0 && e.deltaY < 0;
        const atBottom =
          scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1 && e.deltaY > 0;
        if (atTop || atBottom) {
          e.preventDefault();
        }
      }
    };

    sidebarEl.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      sidebarEl.removeEventListener("wheel", handleWheel);
    };
  }, []);

  const toggleSidebar = (title: any) => {
    localStorage.setItem("menuOpened", title);
    if (title === subOpen) {
      setSubopen("");
    } else {
      setSubopen(title);
    }
  };

  const toggleSubsidebar = (subitem: any) => {
    if (subitem === subsidebar) {
      setSubsidebar("");
    } else {
      setSubsidebar(subitem);
    }
  };

  const toggle = () => {
    dispatch(setExpandMenu(true));
  };

  const toggle2 = () => {
    dispatch(setExpandMenu(false));
  };

  // Filter SidebarData based on permissions
  const filteredSidebarData = SidebarData.map((mainLabel: SidebarSection) => ({
    ...mainLabel,
    submenuItems: mainLabel.submenuItems
      .filter((item: MenuItem) => {
        if (item.permission === undefined || item.permission === true) return true;
        if (hasStringPermission(item)) {
          return checkPermission(item.permission);
        }
        return false;
      })
      .map((item: MenuItem) => ({
        ...item,
        submenuItems: item.submenuItems
          ? item.submenuItems.filter((subItem: MenuItem) => {
            if (subItem.permission === undefined || subItem.permission === true) return true;
            if (hasStringPermission(subItem)) {
              return checkPermission(subItem.permission);
            }
            return false;
          })
          : [],
      })),
  })).filter((mainLabel: SidebarSection) => mainLabel.submenuItems.length > 0);

  return (
    <>
      <div
        className="sidebar"
        id="sidebar"
        onMouseEnter={toggle}
        onMouseLeave={toggle2}
        onWheel={(e) => e.stopPropagation()}
      >
        <Scrollbars>
          <div className="sidebar-inner slimscroll">
            <div id="sidebar-menu" className="sidebar-menu">
              <ul>
                <li className="clinicdropdown theme mb-3">
                  <div className="sidebar-profile-card">
                    <div className="d-flex align-items-center gap-2">
                      <div className="sidebar-avatar-wrap position-relative flex-shrink-0">
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "12px",
                            background: "var(--rm-primary-gradient)",
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.15rem",
                            boxShadow: "0 4px 12px rgba(79, 70, 229, 0.3)",
                          }}
                        >
                          <i className="ti ti-user" />
                        </div>
                        <span
                          style={{
                            position: "absolute",
                            bottom: "-1px",
                            right: "-1px",
                            width: "9px",
                            height: "9px",
                            borderRadius: "50%",
                            background: "#10B981",
                            border: "2px solid #ffffff",
                          }}
                        />
                      </div>
                      <div className="user-names overflow-hidden" style={{ flex: 1 }}>
                        <h5 className="text-truncate fw-bold mb-0" style={{ fontSize: "0.88rem", color: "#1e293b" }}>
                          {username}
                        </h5>
                        <div className="d-flex align-items-center gap-1 mt-1">
                          <span
                            className="badge badge-soft-primary"
                            style={{ fontSize: "0.68rem", padding: "2px 8px", borderRadius: "6px", fontWeight: 600 }}
                          >
                            {group}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>

              <ul>
                {filteredSidebarData.map((mainLabel: SidebarSection, index) => (
                  <li className="clinicdropdown" key={index}>
                    <h6 className="submenu-hdr">{mainLabel?.label}</h6>
                    <ul>
                      {mainLabel?.submenuItems?.map((title: MenuItem, i) => (
                        <li className="submenu" key={title.label}>
                          <Link
                            to={title?.submenu ? "#" : title?.link ?? "#"}
                            onClick={() => toggleSidebar(title?.label)}
                            className={`${subOpen === title?.label ? "subdrop" : ""
                              } ${title?.links?.includes(Location.pathname)
                                ? "active"
                                : ""
                              } ${title?.submenuItems
                                ?.map((link: any) => link?.link)
                                .includes(Location.pathname) ||
                                title?.link === Location.pathname
                                ? "active"
                                : ""
                              } ${title?.subLink1 === Location.pathname
                                ? "active"
                                : ""
                              } ${title?.subLink2 === Location.pathname
                                ? "active"
                                : ""
                              } ${title?.subLink3 === Location.pathname
                                ? "active"
                                : ""
                              } ${title?.subLink4 === Location.pathname
                                ? "active"
                                : ""
                              }`}
                          >
                            <i className={title.icon}></i>
                            <span>{title?.label}</span>
                            <span className={title?.submenu ? "menu-arrow" : ""} />
                          </Link>
                          <ul
                            style={{
                              display: subOpen === title?.label ? "block" : "none",
                            }}
                          >
                            {title?.submenuItems?.map((item: MenuItem) => (
                              <li className="submenu submenu-two" key={item.label}>
                                <Link
                                  to={item?.link ?? "#"}
                                  className={
                                    `${item?.submenuItems
                                      ?.map((link: any) => link?.link)
                                      .includes(Location.pathname) ||
                                      item?.link === Location.pathname
                                      ? "active subdrop"
                                      : ""} `
                                  }
                                  onClick={() => {
                                    toggleSubsidebar(item?.label);
                                  }}
                                >
                                  {item?.label}
                                  <span className={item?.submenu ? "menu-arrow" : ""} />
                                </Link>
                                <ul
                                  style={{
                                    display: subsidebar === item?.label ? "block" : "none",
                                  }}
                                >
                                  {item?.submenuItems?.map((items: MenuItem) => (
                                    <li key={items.label}>
                                      <Link
                                        to={items?.link ?? "#"}
                                        className={`${subsidebar === items?.label
                                            ? "submenu-two subdrop"
                                            : "submenu-two"
                                          } ${items?.submenuItems
                                            ?.map((link: any) => link.link)
                                            .includes(Location.pathname) ||
                                            items?.link === Location.pathname
                                            ? "active"
                                            : ""
                                          }`}
                                      >
                                        {items?.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Scrollbars>
      </div>
    </>
  );
};

export default Sidebar;